import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from 'prop-types';
import { Button, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Badge, Label, Input, InputGroup, InputGroupText, FormText, Modal, ModalHeader, ModalBody, ModalFooter, Tooltip } from 'reactstrap';
import './census-sync.styl';

// Unique ID counter for tooltip targets
let truncatedTextIdCounter = 0;

// Renders text with ellipsis truncation and a Reactstrap Tooltip only when truncated
export function TruncatedText({ text, placeholder, className = "dropdown-toggle-text" }) {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const idRef = useRef(`truncated-text-${++truncatedTextIdCounter}`);

  const checkTruncation = useCallback(() => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkTruncation();
  }, [text, checkTruncation]);

  return (
    <>
      <span
        ref={textRef}
        id={idRef.current}
        className={className}
      >
        {text || placeholder}
      </span>
      {isTruncated && (
        <Tooltip
          isOpen={tooltipOpen}
          target={idRef.current}
          toggle={() => setTooltipOpen(!tooltipOpen)}
          placement="top"
          className="field-name-tooltip"
          innerClassName="field-name-tooltip-inner"
        >
          {text || placeholder}
        </Tooltip>
      )}
    </>
  );
}

// Validate field name for common database/API requirements
// - Must start with a letter or underscore
// - Can only contain letters, numbers, and underscores
// - Max 64 characters (safe limit across most systems)
const validateFieldName = (name) => {
  if (!name || !name.trim()) {
    return { valid: false, error: "Field name is required" };
  }

  const trimmed = name.trim();

  if (trimmed.length > 64) {
    return { valid: false, error: "Field name must be 64 characters or less" };
  }

  if (!/^[a-zA-Z_]/.test(trimmed)) {
    return { valid: false, error: "Field name must start with a letter or underscore" };
  }

  if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmed)) {
    return { valid: false, error: "Field name can only contain letters, numbers, and underscores" };
  }

  return { valid: true, error: null };
};

// Simple searchable dropdown for source columns
function SearchableSourceDropdown({
  isOpen,
  toggle,
  value,
  options,
  onChange,
  placeholder = "Select a column",
  className = "",
  disabled = false,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options?.filter((option) => {
    const name = option.name || option.label || "";
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    onChange(option.name);
    toggle();
  };

  return (
    <Dropdown isOpen={!disabled && isOpen} toggle={disabled ? undefined : toggle} className={className}>
      <DropdownToggle caret color="light" className="w-100 text-left dropdown-toggle-truncate" disabled={disabled}>
        <TruncatedText text={value} placeholder={placeholder} />
      </DropdownToggle>
      <DropdownMenu className="searchable-dropdown-menu">
        <div className="dropdown-search-container">
          <InputGroup size="sm">
            <InputGroupText>
              <i className="bi bi-search" />
            </InputGroupText>
            <Input
              ref={inputRef}
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </InputGroup>
          {filteredOptions.length === 0 && searchTerm.trim() && (
            <FormText color="muted" className="mt-1 ms-1">
              <i className="bi bi-info-circle me-1" />
              No matches found
            </FormText>
          )}
        </div>
        <div className="dropdown-options-container">
          {filteredOptions.map((option) => (
            <DropdownItem
              key={option.name}
              active={value === option.name}
              onClick={() => handleSelect(option)}
            >
              {option.name}
            </DropdownItem>
          ))}
        </div>
      </DropdownMenu>
    </Dropdown>
  );
}

// Searchable dropdown with custom field entry support
function SearchableFieldDropdown({
  isOpen,
  toggle,
  value,
  options,
  usedByMap = {},
  currentIndex,
  onChange,
  onConfirmTakeField,
  placeholder = "Select or type...",
  allowCustom = false,
  hideUsedFields = false, // When true, hides fields used by other mappings instead of showing "in use" badge
  className = "",
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmModal, setConfirmModal] = useState({ open: false, option: null, usedByIndex: null });
  const inputRef = useRef(null);

  // Filter options based on search term and usage status
  const filteredOptions = options?.filter((option) => {
    const label = option.label || option.name || option.full_name || "";
    const fullName = option.full_name || option.name || "";
    const matchesSearch = label.toLowerCase().includes(searchTerm.toLowerCase()) ||
           fullName.toLowerCase().includes(searchTerm.toLowerCase());

    // If hideUsedFields is enabled, filter out fields used by other mappings
    if (hideUsedFields) {
      const fieldKey = option.full_name || option.name;
      const usedByIndex = usedByMap[fieldKey];
      const isUsedByOther = usedByIndex !== undefined && usedByIndex !== currentIndex;
      return matchesSearch && !isUsedByOther;
    }

    return matchesSearch;
  }) || [];

  // Check if search term matches an existing option (case-insensitive)
  const exactMatch = options?.find((option) => {
    const label = option.label || option.name || "";
    const fullName = option.full_name || option.name || "";
    return label.toLowerCase() === searchTerm.trim().toLowerCase() ||
           fullName.toLowerCase() === searchTerm.trim().toLowerCase();
  });

  // Check if search term matches a field that's hidden because it's in use by another mapping
  const matchesHiddenInUseField = hideUsedFields && searchTerm.trim() && options?.some((option) => {
    const label = (option.label || option.name || "").toLowerCase();
    const fullName = (option.full_name || option.name || "").toLowerCase();
    const shortName = (option.full_name || "").split('.').pop()?.toLowerCase() || "";
    const searchLower = searchTerm.trim().toLowerCase();
    const fieldKey = option.full_name || option.name;
    const usedByIndex = usedByMap[fieldKey];
    const isUsedByOther = usedByIndex !== undefined && usedByIndex !== currentIndex;

    // Check if this field matches the search AND is used by another mapping
    const matchesSearch = label.includes(searchLower) || fullName.includes(searchLower) || shortName.includes(searchLower);
    return matchesSearch && isUsedByOther;
  });

  // Check if the custom field name would duplicate an existing destination field
  const isDuplicateField = searchTerm.trim() && options?.some((option) => {
    const label = (option.label || option.name || "").toLowerCase();
    const fullName = (option.full_name || option.name || "").toLowerCase();
    const shortName = (option.full_name || "").split('.').pop()?.toLowerCase() || "";
    const searchLower = searchTerm.trim().toLowerCase();
    return label === searchLower || fullName === searchLower || shortName === searchLower;
  });

  // Validate custom field name
  const fieldValidation = validateFieldName(searchTerm);
  const canCreateCustom = allowCustom &&
    searchTerm.trim() &&
    !exactMatch &&
    !isDuplicateField &&
    fieldValidation.valid;

  // Determine what message to show for the create option
  const getCreateOptionState = () => {
    if (!allowCustom || !searchTerm.trim()) return null;
    if (exactMatch) return null;
    if (isDuplicateField) return { error: "A field with this name already exists" };
    if (!fieldValidation.valid) return { error: fieldValidation.error };
    return { valid: true };
  };

  const createOptionState = getCreateOptionState();

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    const fieldKey = option.full_name || option.name;
    const usedByIndex = usedByMap[fieldKey];

    // Check if field is used by another mapping (not current one)
    if (usedByIndex !== undefined && usedByIndex !== currentIndex) {
      // Close dropdown first, then show modal
      toggle();
      setConfirmModal({ open: true, option, usedByIndex });
      return;
    }

    onChange({
      value: fieldKey,
      label: option.label || option.name,
      isCustom: false,
    });
    toggle();
  };

  const handleConfirmTake = () => {
    const { option, usedByIndex } = confirmModal;
    // Clear the field from the other mapping
    if (onConfirmTakeField) {
      onConfirmTakeField(usedByIndex);
    }
    // Set the field on current mapping
    onChange({
      value: option.full_name || option.name,
      label: option.label || option.name,
      isCustom: false,
    });
    setConfirmModal({ open: false, option: null, usedByIndex: null });
  };

  const handleCancelTake = () => {
    setConfirmModal({ open: false, option: null, usedByIndex: null });
  };

  const handleCreateCustom = () => {
    onChange({
      value: searchTerm.trim(),
      label: searchTerm.trim(),
      isCustom: true,
    });
    toggle();
  };

  // Get display label for current value
  const displayValue = (() => {
    if (!value) return null;
    const option = options?.find(o => o.full_name === value || o.name === value);
    return option?.label || value;
  })();

  return (
    <>
      <Dropdown isOpen={isOpen} toggle={toggle} className={className}>
        <DropdownToggle caret color="light" className="w-100 text-left dropdown-toggle-truncate">
          <TruncatedText text={displayValue} placeholder={placeholder} />
        </DropdownToggle>
        <DropdownMenu className="searchable-dropdown-menu">
          <div className="dropdown-search-container">
            <InputGroup size="sm" className={createOptionState?.error ? 'has-error' : ''}>
              <InputGroupText>
                <i className="bi bi-search" />
              </InputGroupText>
              <Input
                ref={inputRef}
                type="text"
                placeholder={allowCustom ? "Search or type new name..." : "Search..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canCreateCustom) {
                    e.preventDefault();
                    handleCreateCustom();
                  }
                }}
              />
            </InputGroup>
            {filteredOptions.length === 0 && searchTerm.trim() && !matchesHiddenInUseField && !createOptionState?.error && (
              <FormText color="muted" className="mt-1 ms-1">
                <i className="bi bi-info-circle me-1" />
                {createOptionState?.valid ? 'Field not found' : 'No matches found'}
              </FormText>
            )}
            {filteredOptions.length === 0 && searchTerm.trim() && matchesHiddenInUseField && (
              <FormText color="muted" className="mt-1 ms-1">
                <i className="bi bi-exclamation-triangle me-1" />
                Field already in use
              </FormText>
            )}
            {createOptionState?.error && (
              <FormText color="danger" className="mt-1 ms-1">
                <i className="bi bi-exclamation-triangle me-1" />
                {createOptionState.error}
              </FormText>
            )}
          </div>
          <div className="dropdown-options-container">
            {createOptionState?.valid && (
              <DropdownItem onClick={handleCreateCustom} className="create-custom-option">
                <i className="bi bi-plus-circle me-2" />
                Create "{searchTerm.trim()}"
              </DropdownItem>
            )}
            {filteredOptions.map((option) => {
              const fieldKey = option.full_name || option.name;
              const usedByIndex = usedByMap[fieldKey];
              const isUsedByOther = usedByIndex !== undefined && usedByIndex !== currentIndex;
              const isCurrentValue = value === fieldKey;

              return (
                <DropdownItem
                  key={fieldKey}
                  active={isCurrentValue}
                  onClick={() => handleSelect(option)}
                  className={isUsedByOther ? "field-in-use" : ""}
                >
                  <span>{option.label || option.name}</span>
                  {isUsedByOther && (
                    <Badge color="warning" className="ms-2 in-use-badge">in use</Badge>
                  )}
                </DropdownItem>
              );
            })}
          </div>
        </DropdownMenu>
      </Dropdown>

      <Modal isOpen={confirmModal.open} toggle={handleCancelTake} size="sm">
        <ModalHeader toggle={handleCancelTake}>Field In Use</ModalHeader>
        <ModalBody>
          <p>
            <strong>{confirmModal.option?.label || confirmModal.option?.name}</strong> is already mapped in another row.
          </p>
          <p>Do you want to use it here instead? It will be removed from the other mapping.</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCancelTake}>Cancel</Button>
          <Button color="primary" onClick={handleConfirmTake}>Use Here</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default function SingleFieldMapper({
  mapping,
  index,
  columns,
  allDestinationFields,
  usedDestinationFieldsMap,
  getAvailableFieldsForMapping,
  updateAdditionalMapping,
  clearMappingDestination,
  removeMapping,
  incompleteLabel,
  allowCustomFields = false,
  disableSource = false,
  hideUsedDestinationFields = true, // When true, hides used fields instead of showing "in use" badge with confirmation
  showMoveControls = false,
  dragHandleProps = null,
  isDragging = false,
}) {
  const isRequired = mapping.isRequired === true;
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
  const [destinationDropdownOpen, setDestinationDropdownOpen] = useState(false);

  // Auto-select source/destination defaults when data becomes available
  useEffect(() => {
    const availableFields = getAvailableFieldsForMapping(index);

    // If both are empty but we have columns and available fields, find a matching pair
    if (!mapping.from && !mapping.to && columns?.length && availableFields?.length) {
      let matched = false;
      for (const col of columns) {
        const matchingField = availableFields.find(
          field => field.label?.toLowerCase() === col.name.toLowerCase() ||
                   field.full_name?.toLowerCase() === col.name.toLowerCase() ||
                   field.full_name?.split('.').pop()?.toLowerCase() === col.name.toLowerCase()
        );
        if (matchingField) {
          updateAdditionalMapping(index, "from", col.name);
          updateAdditionalMapping(index, "to", matchingField.full_name);
          matched = true;
          break;
        }
      }
      // If no match found, fall back to column/field at the same index position
      if (!matched) {
        const colIndex = index === -1 ? 0 : index;
        const col = columns[colIndex];
        const field = availableFields[colIndex];
        if (col) {
          updateAdditionalMapping(index, "from", col.name);
        }
        if (field) {
          updateAdditionalMapping(index, "to", field.full_name);
        }
      }
      return;
    }

    // If destination is set but source is empty, try to find matching source column
    if (mapping.to && !mapping.from && columns?.length) {
      // Extract field name from full_name (e.g., "object.field" -> "field")
      const toFieldName = mapping.to.split('.').pop()?.toLowerCase();
      const matchingColumn = columns.find(
        col => col.name.toLowerCase() === toFieldName ||
               col.name.toLowerCase() === mapping.to.toLowerCase()
      );
      if (matchingColumn) {
        updateAdditionalMapping(index, "from", matchingColumn.name);
      }
    }

    // If source is set but destination is empty, try to find matching destination field
    if (mapping.from && !mapping.to && availableFields?.length) {
      const matchingField = availableFields.find(
        field => field.label?.toLowerCase() === mapping.from.toLowerCase() ||
                 field.full_name?.toLowerCase() === mapping.from.toLowerCase() ||
                 field.full_name?.split('.').pop()?.toLowerCase() === mapping.from.toLowerCase()
      );
      if (matchingField) {
        updateAdditionalMapping(index, "to", matchingField.full_name);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapping.from, mapping.to, columns?.length]);

  const toggleSourceDropdown = () => setSourceDropdownOpen(!sourceDropdownOpen);
  const toggleDestinationDropdown = () => setDestinationDropdownOpen(!destinationDropdownOpen);

  return (
    <div
      key={index}
      className={`mapping-row ${isDragging ? 'is-dragging' : ''}`}
    >
      {/* Drag handle - only show when in mapping order mode */}
      {showMoveControls && (
        <div className="move-controls" {...(dragHandleProps || {})}>
          <i className="bi bi-grip-vertical drag-handle" />
        </div>
      )}
      {/* Show required badge or incomplete label */}
      {isRequired && (
        <Badge color="warning" className="required-badge">
          <i className="fa-solid fa-asterisk" />
          <span>REQUIRED FIELD</span>
        </Badge>
      )}
      {incompleteLabel}
      <div className="mapping-fields">
        <div>
          <Label className="field-label">
            Source Column{isRequired && <span className="required-asterisk"> *</span>}
          </Label>
          <SearchableSourceDropdown
            isOpen={sourceDropdownOpen}
            toggle={toggleSourceDropdown}
            value={mapping.from}
            options={columns}
            onChange={(value) => updateAdditionalMapping(index, "from", value)}
            placeholder="Select a column"
            className={isRequired ? "border-orange-300" : ""}
            disabled={disableSource}
          />
        </div>
        <i className="mapping-icon bi bi-chevron-right" />
        <div>
          <label className="field-label">
            Destination Field{isRequired && <span className="required-asterisk"> *</span>}
            {mapping.isAutoCreated ? (
              <Badge className="added-field badge-auto-created">+ Auto-created Field</Badge>
            ) : mapping.isCustom ? (
              <Badge className="added-field badge-custom">+ Custom Field</Badge>
            ) : mapping.isAutoMapped ? (
              <Badge className="added-field badge-matched">= Matched Field</Badge>
            ) : null}
          </label>
          <SearchableFieldDropdown
            isOpen={destinationDropdownOpen}
            toggle={toggleDestinationDropdown}
            value={mapping.to}
            options={allDestinationFields || getAvailableFieldsForMapping(index)}
            usedByMap={usedDestinationFieldsMap || {}}
            currentIndex={index}
            onChange={({ value, isCustom }) => {
              updateAdditionalMapping(index, "to", value);
              updateAdditionalMapping(index, "isCustom", isCustom);
              updateAdditionalMapping(index, "isAutoCreated", false); // Clear auto-created flag on manual change
            }}
            onConfirmTakeField={(otherIndex) => {
              if (clearMappingDestination) {
                clearMappingDestination(otherIndex);
              }
            }}
            placeholder="Select a field"
            allowCustom={allowCustomFields}
            hideUsedFields={hideUsedDestinationFields} 
            className={isRequired ? "border-orange-300" : ""}
          />
        </div>
      </div>
      {/* Remove button - only show for non-required mappings */}
      <div className="remove-button">
        {!isRequired && (
          <Button onClick={() => removeMapping(index)} color="link" size="small" className="btn-link-census">
            <i className="bi bi-trash" />
          </Button>
        )}
      </div>
    </div>
  );
}

SingleFieldMapper.propTypes = {
  mapping: PropTypes.shape({
    from: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
    isRequired: PropTypes.bool,
    isCustom: PropTypes.bool,
    isAutoCreated: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  columns: PropTypes.array.isRequired,
  allDestinationFields: PropTypes.array,
  usedDestinationFieldsMap: PropTypes.object,
  getAvailableFieldsForMapping: PropTypes.func.isRequired,
  updateAdditionalMapping: PropTypes.func.isRequired,
  clearMappingDestination: PropTypes.func,
  removeMapping: PropTypes.func.isRequired,
  incompleteLabel: PropTypes.node,
  allowCustomFields: PropTypes.bool,
  disableSource: PropTypes.bool,
  hideUsedDestinationFields: PropTypes.bool,
  showMoveControls: PropTypes.bool,
  dragHandleProps: PropTypes.object,
  isDragging: PropTypes.bool,
};

